import type { FC } from 'react'
import { useState } from 'react'
import { Button, Checkbox, type CheckboxProps, HStack, VStack } from '@chakra-ui/react'
import { CheckboxIcon, GrandText } from 'ui'
import { BiChevronDown, BiChevronUp } from 'react-icons/bi'
import { BsCheckLg } from 'react-icons/bs'
import type { ServiceDTO } from 'ecosystem'
import { Currency } from 'ecosystem'
import { commaSeparator } from 'shared-utils'
import { overrideText } from 'ui/lib/overrides'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import { useUIGlobalsContext } from 'ui/lib/ui-globals/hooks'

interface ICheckoutCartServiceProps {
  service?: ServiceDTO
  checkboxProps?: CheckboxProps
}

export interface CartServiceOverrides {
  cartServiceShowDetails?: string
  cartServiceHideDetails?: string
  currency?: string
}

export const CheckoutCartService: FC<ICheckoutCartServiceProps> = ({ service, checkboxProps }) => {
  const overrides = useOverridesContext<keyof CartServiceOverrides>()
  const { priceMaximumFractionDigits } = useUIGlobalsContext()

  const [showDetails, setShowDetails] = useState(false)
  if (!service) {
    return null
  }

  return (
    <HStack w="full">
      <VStack display={{ base: 'flex', md: 'none' }} flexGrow={1} fontSize="xs" spacing={1}>
        <HStack justify="space-between" w="full">
          <GrandText fontWeight="bold">{service.name}</GrandText>
          <HStack>
            <GrandText fontWeight="bold" minW={20} textAlign="right" w="auto">
              {commaSeparator(service.price, priceMaximumFractionDigits)}{' '}
              {overrideText(Currency.SEK, overrides?.currency)}
            </GrandText>
            <Checkbox
              borderColor="primary"
              borderRadius="sm"
              display="flex"
              fontSize={12}
              icon={<CheckboxIcon as={BsCheckLg} />}
              justifyContent="center"
              name={service.id}
              size="lg"
              {...checkboxProps}
            />
          </HStack>
        </HStack>
        <GrandText
          h={showDetails ? 150 : 0}
          overflowY="auto"
          transition="all 0.1s cubic-bezier(.17,.67,.83,.67)"
        >
          {service.description}
        </GrandText>
        <Button
          alignSelf="flex-start"
          aria-label={`${showDetails ? 'show' : 'hide'}-service-details`}
          bg="transparent"
          color="text.mild"
          onClick={() => {
            setShowDetails(!showDetails)
          }}
          rightIcon={showDetails ? <BiChevronUp /> : <BiChevronDown />}
          textDecoration="none"
        >
          {showDetails
            ? overrideText('gömma detaljer', overrides?.cartServiceHideDetails)
            : overrideText('visa detaljer', overrides?.cartServiceShowDetails)}
        </Button>
      </VStack>

      <HStack align="center" display={{ base: 'none', md: 'flex' }} w="full">
        <VStack flexGrow={1} fontSize="sm">
          <GrandText fontWeight="bold">{service.name}</GrandText>
          <GrandText>{service.description}</GrandText>
        </VStack>

        <GrandText fontSize="sm" fontWeight="bold" minW={20} textAlign="right" w="auto">
          {commaSeparator(service.price, priceMaximumFractionDigits)}{' '}
          {overrideText(Currency.SEK, overrides?.currency)}
        </GrandText>
        <Checkbox
          borderColor="primary"
          borderRadius="sm"
          display="flex"
          fontSize={12}
          icon={<CheckboxIcon as={BsCheckLg} />}
          justifyContent="center"
          name={service.id}
          size="lg"
          {...checkboxProps}
        />
      </HStack>
    </HStack>
  )
}
